import * as styles from "./PdfViewerDocument.css";

import { clsx } from "clsx";
import { type JSX, useEffect, useRef } from "react";

import { useDocumentState } from "@embedpdf/core/react";
import { useScroll } from "@embedpdf/plugin-scroll/react";
import { useZoom } from "@embedpdf/plugin-zoom/react";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { useLingui } from "@lingui/react/macro";

import { usePdfViewerContext } from "./PdfViewer.context";
import type {
  PdfViewerDocumentInfo,
  PdfViewerErrorDetail,
} from "./PdfViewer.types";
import { PdfViewerPages } from "./PdfViewerPages";
import { PdfViewerThumbnails } from "./PdfViewerThumbnails";
import { PdfViewerToolbar } from "./PdfViewerToolbar";

type PdfViewerDocumentProps = {
  defaultPage: number;
  toolbar: boolean;
  onPageChange?: (page: number) => void;
  onZoomChange?: (zoom: number) => void;
  onDocumentLoad?: (info: PdfViewerDocumentInfo) => void;
  onError?: (error: PdfViewerErrorDetail) => void;
};

export function PdfViewerDocument({
  defaultPage,
  toolbar,
  onPageChange,
  onZoomChange,
  onDocumentLoad,
  onError,
}: PdfViewerDocumentProps): JSX.Element {
  const { t } = useLingui();

  const { documentId, thumbnailsOpen } = usePdfViewerContext();

  const documentState = useDocumentState(documentId);

  const { provides: scroll, state: scrollState } = useScroll(documentId);
  const { state: zoomState } = useZoom(documentId);

  const status = documentState?.status ?? "loading";

  const pageCount = documentState?.document?.pageCount ?? 0;

  useEffect(() => {
    if (status === "loaded" && pageCount > 0) {
      onDocumentLoad?.({ documentId, pageCount });
    }
  }, [status, pageCount, documentId, onDocumentLoad]);

  useEffect(() => {
    if (status === "error") {
      onError?.({
        message: documentState?.error ?? "",
        code: documentState?.errorCode,
      });
    }
  }, [status, documentState?.error, documentState?.errorCode, onError]);

  useEffect(() => {
    onPageChange?.(scrollState.currentPage);
  }, [scrollState.currentPage, onPageChange]);

  useEffect(() => {
    onZoomChange?.(zoomState.currentZoomLevel);
  }, [zoomState.currentZoomLevel, onZoomChange]);

  // The initial page is honoured once, so a later scroll is never undone.
  const jumped = useRef(false);

  useEffect(() => {
    if (jumped.current || scroll === null || status !== "loaded") {
      return;
    }

    jumped.current = true;

    if (defaultPage > 1) {
      scroll.scrollToPage({ pageNumber: defaultPage, behavior: "instant" });
    }
  }, [scroll, status, defaultPage]);

  if (status === "error") {
    return (
      <p className={clsx(styles.state, styles.stateError)}>
        {t`This document could not be opened.`}
        {documentState?.error ? (
          <span className={styles.stateDetail}>{documentState.error}</span>
        ) : null}
      </p>
    );
  }

  return (
    <>
      {toolbar ? <PdfViewerToolbar pageCount={pageCount} /> : null}

      <div className={styles.body}>
        {thumbnailsOpen ? <PdfViewerThumbnails /> : null}

        {status === "loading" ? (
          <p className={styles.state}>
            <Spinner />
            {t`Loading the document`}
          </p>
        ) : (
          <PdfViewerPages />
        )}
      </div>
    </>
  );
}
